import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { buildStudentMergeView, getStudentMergeSuggestions } from './studentMergePlan';
import './mergeStudents.scss';

const nameOf = student => `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.id;

function MergeStudentsSection({ studentsA, studentsB, blockers, decisions, onDecisionChange, disabled }) {
  const { t } = useTranslation('manage');
  const view = buildStudentMergeView({ studentsA, studentsB, blockers }, decisions);
  const suggestions = getStudentMergeSuggestions(studentsA, studentsB);
  const targets = new Set(Object.values(decisions).filter(d => d.merge).map(d => d.mergeWith));
  const card = student => (student ? (
    <span
      className="merge-students-name"
      title={[nameOf(student), `ID: ${student.id}`, student.additionalInfo,
        student.archived && t('mergeStudents.archived')].filter(Boolean).join(' · ')}
    >
      {nameOf(student)}
      {student.archived && <span className="badge badge-secondary ml-2">{t('mergeStudents.archived')}</span>}
    </span>
  ) : <span aria-label={t('mergeStudents.noStudent')}>—</span>);
  return (
    <section className="merge-students-section" aria-labelledby="merge-students-title">
      <h5 id="merge-students-title">{t('mergeStudents.title')}</h5>
      <p className="text-muted">{t('mergeStudents.explanation')}</p>
      {(studentsA.some(s => s.archived) || studentsB.some(s => s.archived)) && <p>{t('mergeStudents.archivePolicy')}</p>}
      <div className="merge-students-head" aria-hidden="true">
        <strong>{t('mergeStudents.clientA')}</strong><strong>{t('mergeStudents.clientB')}</strong><strong>{t('mergeStudents.result')}</strong>
      </div>
      {view.rows.length === 0 && <p>{t('mergeStudents.empty')}</p>}
      {view.rows.map(row => (
        <div className={`merge-students-row merge-students-row--${row.status}`} key={row.key}>
          <div><span className="merge-students-mobile-label">{t('mergeStudents.clientA')}</span>{card(row.studentA)}</div>
          <div className="merge-students-cell">
            <span className="merge-students-mobile-label">{t('mergeStudents.clientB')}</span>{card(row.studentB)}
            {row.studentB && !view.shared.has(row.studentB.id) && (
              <div className="merge-students-controls">
                <label className="sr-only" htmlFor={`merge-destination-${row.studentB.id}`}>{t('mergeStudents.action')}</label>
                <select
                  id={`merge-destination-${row.studentB.id}`}
                  className="form-control form-control-sm"
                  title={suggestions.filter(s => s.studentBId === row.studentB.id).map(s => t(`mergeStudents.${s.reason}`, { name: nameOf(studentsA.find(a => a.id === s.studentAId)) })).join(" ")}
                  aria-label={t('mergeStudents.destinationFor', { name: nameOf(row.studentB) })}
                  value={(decisions[row.studentB.id] || {}).mergeWith || ''}
                  disabled={disabled}
                  onChange={event => onDecisionChange(row.studentB.id, { merge: !!event.target.value, mergeWith: event.target.value || null })}
                >
                  <option value="">{t('mergeStudents.transferSeparately')}</option>
                  {studentsA.filter(a => !view.shared.has(a.id)).map(a => (
                    <option
                      key={a.id}
                      value={a.id}
                      disabled={
                      (targets.has(a.id) && (decisions[row.studentB.id] || {}).mergeWith !== a.id) ||
                      blockers.some(item => item.studentBId === row.studentB.id && item.studentAId === a.id)
                    }
                    >{t('mergeStudents.mergeWith', { name: nameOf(a), id: a.id })}
                      {a.archived ? ` (${t('mergeStudents.archived')})` : ''}
                    </option>
                  ))}
                </select>
                {blockers.filter(item => item.studentBId === row.studentB.id).map(item => (
                  <small className="d-block text-danger" key={`${item.code}-${item.studentAId}`}>
                    {t('mergeStudents.pairBlocked', { name: nameOf(studentsA.find(a => a.id === item.studentAId) || { id: item.studentAId }) })}
                    {' '}{t(`mergeStudents.errors.${item.code}`)}
                  </small>
                ))}
              </div>
            )}
          </div>
          <div className="merge-students-cell">
            <span className="merge-students-mobile-label">{t('mergeStudents.result')}</span>
            <span className="sr-only">{t(`mergeStudents.${row.status}`)}</span>{card(row.result)}
            {row.status === 'paired' && (
            <button
              type="button"
              className="btn btn-sm manage-teal-button"
              title={t('mergeStudents.keepA')}
              disabled={disabled}
              onClick={() => onDecisionChange(row.studentB.id, { merge: false, mergeWith: null })}
            >{t('mergeStudents.disconnect')}
            </button>
            )}
          </div>
        </div>
      ))}
      <div className="merge-students-summary mt-3" aria-live="polite">
        <strong>{t('mergeStudents.total', { count: view.counts.total })}</strong>
        <div>{t('mergeStudents.summary', view.counts)}</div>
        {view.counts.archived > 0 && <div>{t('mergeStudents.archiveSummary', view.counts)}</div>}
      </div>
      {view.errors.map(code => <p role="alert" className="text-danger mt-2" key={code}>{t(`mergeStudents.errors.${code}`)}</p>)}
    </section>
  );
}
MergeStudentsSection.propTypes = {
  studentsA: PropTypes.array.isRequired,
  studentsB: PropTypes.array.isRequired,
  blockers: PropTypes.array,
  decisions: PropTypes.object.isRequired,
  onDecisionChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
MergeStudentsSection.defaultProps = { blockers: [], disabled: false };
export default MergeStudentsSection;
